"use client";

import { Close, Content, Description, Title } from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import React from "react";

import { cn } from "../../../utils";
import { Icon } from "../../Icon";
import { Typography } from "../../Typography";
import { ModalContentAnimation } from "./ModalContentAnimation";

export interface ModalContentProps {
  children: ReactNode;
  description?: string | null;
  header?: ReactNode | string;
  footer?: ReactNode;
  exitDelay?: number;
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  footerClassName?: string;
  disableBackdropClick?: boolean;
  shouldScroll?: boolean;
  isDismissible?: boolean;
}

export const ModalContent: React.FC<ModalContentProps> = ({
  children,
  header,
  description,
  footer,
  exitDelay,
  className,
  headerClassName,
  titleClassName,
  footerClassName,
  disableBackdropClick,
  shouldScroll = true,
  isDismissible = true,
}) => {
  return (
    <ModalContentAnimation exitDelay={exitDelay}>
      <Content
        asChild
        onInteractOutside={(e) => {
          if (disableBackdropClick) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (
            e.target instanceof Element &&
            (e.target as Element).closest("[data-sonner-toast]")
          ) {
            e.preventDefault();
          }
        }}
      >
        <div
          className={cn(
            "ui:fixed ui:bottom-0 ui:left-0 ui:flex ui:max-h-[80vh] ui:w-full ui:flex-col ui:overflow-hidden ui:rounded-t-md ui:border-divider ui:bg-white ui:px-4 ui:py-5 ui:focus:outline-none ui:sm:top-1/2 ui:sm:bottom-auto ui:sm:left-1/2 ui:sm:max-h-[calc(100vh-24px)] ui:sm:max-w-lg ui:sm:-translate-1/2 ui:sm:rounded-sm ui:sm:p-8 ui:dark:border ui:dark:bg-black",
            className,
          )}
        >
          {header && (
            <div
              className={cn(
                "ui:flex ui:flex-col ui:space-y-1.5 ui:text-center ui:sm:text-left",
                headerClassName,
              )}
            >
              {typeof header === "string" ? (
                <Title
                  className={cn(
                    "ui:-mx-4 ui:mb-3 ui:border-b ui:border-divider ui:pb-5.25 ui:pl-4 ui:text-left ui:font-grotesk ui:text-sm ui:font-semibold ui:tracking-tight ui:sm:border-b-0 ui:sm:pb-0 ui:sm:pl-0 ui:sm:text-center ui:sm:text-2xl ui:sm:text-textPrimary",
                    titleClassName,
                  )}
                >
                  {header}
                </Title>
              ) : (
                <Title asChild>{header}</Title>
              )}

              <Description>
                {Boolean(description) && <Typography>{description}</Typography>}
              </Description>
            </div>
          )}
          {!header && (
            <>
              <Title className="ui:sr-only">Dialog</Title>
              <Description className="ui:sr-only">Dialog content</Description>
            </>
          )}
          {shouldScroll && (
            <div className="ui:size-full ui:overflow-y-auto">{children}</div>
          )}
          {!shouldScroll && children}
          {footer && (
            <div
              className={cn(
                "ui:flex ui:flex-col-reverse ui:sm:flex-row ui:sm:justify-end ui:sm:space-x-2",
                footerClassName,
              )}
            >
              {footer}
            </div>
          )}
          {isDismissible && (
            <Close
              data-testid="close-dialog"
              className="ui:absolute ui:top-1.75 ui:right-1 ui:flex ui:size-10 ui:cursor-pointer ui:items-center ui:justify-center ui:rounded-full ui:bg-white ui:p-1.5 ui:opacity-70 ui:ring-offset-background ui:transition-all ui:hover:bg-buttonHover ui:sm:top-4 ui:sm:right-4 ui:dark:bg-buttonBg"
            >
              <Icon name="cross" className="ui:text-[#787878]" />
              <span className="ui:sr-only">Close</span>
            </Close>
          )}
        </div>
      </Content>
    </ModalContentAnimation>
  );
};
